import { useState, useEffect } from 'react';
import { api } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Link2, Plus, Search, Edit, Trash2, Eye, X, RefreshCw, CheckCircle2, Key, Lock } from 'lucide-react';

interface Integration {
  id: number;
  platform: string;
  storeName: string;
  storeUrl: string;
  hasApiKey?: boolean;
  hasApiSecret?: boolean;
  hasAccessToken?: boolean;
  webhookUrl?: string;
  configData?: string;
  isActive: boolean;
  autoSync: boolean;
  syncIntervalMinutes: number;
  lastSyncAt?: string;
  lastSyncStatus?: string;
  totalOrdersSynced: number;
  createdAt: string;
}

const T = {
  title: "التكاملات",
  subtitle: "إدارة التكامل مع المتاجر الإلكترونية",
  createNew: "إضافة تكامل جديد",
  totalIntegrations: "إجمالي التكاملات",
  activeIntegrations: "التكاملات النشطة",
  totalOrders: "إجمالي الطلبات المتزامنة",
  autoSync: "المزامنة التلقائية",
  search: "بحث باسم المتجر...",
  loading: "جاري التحميل...",
  noIntegrations: "لا توجد تكاملات",
  thStore: "المتجر",
  thPlatform: "المنصة",
  thUrl: "الرابط",
  thSync: "المزامنة",
  thStatus: "الحالة",
  thOrders: "الطلبات",
  thActions: "الإجراءات",
  view: "عرض",
  edit: "تعديل",
  delete: "حذف",
  sync: "مزامنة",
  editIntegration: "تعديل التكامل",
  createIntegration: "إضافة تكامل جديد",
  viewIntegration: "تفاصيل التكامل",
  lblPlatform: "المنصة",
  lblStoreName: "اسم المتجر",
  lblStoreUrl: "رابط المتجر",
  lblApiKey: "API Key",
  lblApiSecret: "API Secret",
  lblAccessToken: "Access Token",
  lblWebhookUrl: "Webhook URL",
  lblConfigData: "إعدادات إضافية (JSON)",
  lblSyncInterval: "فترة المزامنة (دقائق)",
  cancel: "إلغاء",
  update: "تحديث",
  create: "إضافة",
  close: "إغلاق",
  confirmDelete: "هل أنت متأكد من حذف هذا التكامل؟",
  updated: "تم تحديث التكامل بنجاح",
  created: "تم إضافة التكامل بنجاح",
  deleted: "تم حذف التكامل بنجاح",
  syncing: "جاري المزامنة...",
  syncSuccess: "تمت المزامنة بنجاح",
  error: "حدث خطأ",
  active: "نشط",
  inactive: "غير نشط",
  enabled: "مفعل",
  disabled: "معطل",
  lastSync: "آخر مزامنة",
  never: "لم يتم المزامنة بعد",
  minutes: "دقيقة",
  orders: "طلب",
  apiCredentials: "بيانات API",
  showApi: "إظهار بيانات API",
  hideApi: "إخفاء بيانات API",
  placeholderApiKey: "أدخل API Key",
  placeholderApiSecret: "أدخل API Secret",
  placeholderAccessToken: "أدخل Access Token",
  placeholderWebhook: "https://yourstore.com/webhook"
};

export default function IntegrationsPage() {
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showApiFields, setShowApiFields] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);
  const [selectedIntegration, setSelectedIntegration] = useState<Integration | null>(null);
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [showApiSecret, setShowApiSecret] = useState(false);
  const [form, setForm] = useState({
    platform: 'Shopify', storeName: '', storeUrl: '',
    apiKey: '', apiSecret: '', accessToken: '', webhookUrl: '', configData: '',
    isActive: true, autoSync: false, syncIntervalMinutes: '60'
  });

  useEffect(() => { loadIntegrations(); }, []);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/integrations?pageSize=100');
      if (res.data?.success) {
        const data = res.data.data;
        setIntegrations(Array.isArray(data) ? data : (data.items || []));
      }
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const openCreate = () => {
    setEditingIntegration(null);
    setForm({ platform: 'Shopify', storeName: '', storeUrl: '', apiKey: '', apiSecret: '', accessToken: '', webhookUrl: '', configData: '', isActive: true, autoSync: false, syncIntervalMinutes: '60' });
    setShowApiFields(false);
    setShowModal(true);
  };

  const openEdit = (i: Integration) => {
    setEditingIntegration(i);
    setForm({
      platform: i.platform || 'Shopify',
      storeName: i.storeName || '',
      storeUrl: i.storeUrl || '',
      apiKey: '',
      apiSecret: '',
      accessToken: '',
      webhookUrl: i.webhookUrl || '',
      configData: i.configData || '',
      isActive: i.isActive,
      autoSync: i.autoSync,
      syncIntervalMinutes: i.syncIntervalMinutes.toString()
    });
    setShowApiFields(true);
    setShowModal(true);
  };

  const openView = (i: Integration) => { setSelectedIntegration(i); setShowViewModal(true); };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const payload: Record<string, unknown> = {
        platform: form.platform,
        storeName: form.storeName,
        storeUrl: form.storeUrl,
        webhookUrl: form.webhookUrl,
        configData: form.configData,
        isActive: form.isActive,
        autoSync: form.autoSync,
        syncIntervalMinutes: parseInt(form.syncIntervalMinutes) || 60
      };
      if (form.apiKey.trim()) payload.apiKey = form.apiKey.trim();
      if (form.apiSecret.trim()) payload.apiSecret = form.apiSecret.trim();
      if (form.accessToken.trim()) payload.accessToken = form.accessToken.trim();
      if (editingIntegration) {
        await api.put('/api/integrations/' + editingIntegration.id, payload);
        alert(T.updated);
      } else {
        await api.post('/api/integrations', payload);
        alert(T.created);
      }
      setShowModal(false);
      loadIntegrations();
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(T.confirmDelete)) return;
    try {
      await api.delete('/api/integrations/' + id);
      loadIntegrations();
      alert(T.deleted);
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    }
  };

  const handleSync = async (id: number) => {
    setSyncingId(id);
    try {
      await api.post('/api/integrations/' + id + '/sync');
      loadIntegrations();
      alert(T.syncSuccess);
    } catch (err: any) {
      alert(err.response?.data?.message || T.error);
    } finally {
      setSyncingId(null);
    }
  };

  const filtered = integrations.filter(i =>
    (i.storeName || '').toLowerCase().includes(search.toLowerCase()) ||
    (i.platform || '').toLowerCase().includes(search.toLowerCase())
  );

  const activeIntegrations = integrations.filter(i => i.isActive).length;
  const totalOrders = integrations.reduce((sum, i) => sum + (i.totalOrdersSynced || 0), 0);
  const autoSyncCount = integrations.filter(i => i.autoSync).length;
  const inputClass = 'w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-cyan-500 transition';

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return T.never;
    return new Date(dateStr).toLocaleString('ar-EG');
  };

  const maskSecretStatus = (available?: boolean) => available ? 'مخزن بأمان' : 'غير مضبوط';

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg">
            <Link2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{T.title}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{T.subtitle}</p>
          </div>
        </h1>
        <Button onClick={openCreate} className="bg-gradient-to-l from-cyan-500 to-cyan-600 hover:from-cyan-600 hover:to-cyan-700 text-white px-6 py-3 rounded-xl font-semibold shadow-lg">
          <Plus className="w-5 h-5 ml-2" />
          <span>{T.createNew}</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-cyan-100 flex items-center justify-center"><Link2 className="w-5 h-5 text-cyan-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalIntegrations}</p>
          <p className="text-2xl font-bold text-gray-900">{integrations.length}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center"><CheckCircle2 className="w-5 h-5 text-green-600" /></div></div>
          <p className="text-sm text-gray-500">{T.activeIntegrations}</p>
          <p className="text-2xl font-bold text-green-700">{activeIntegrations}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center"><RefreshCw className="w-5 h-5 text-blue-600" /></div></div>
          <p className="text-sm text-gray-500">{T.totalOrders}</p>
          <p className="text-2xl font-bold text-blue-700">{totalOrders.toLocaleString()}</p>
        </CardContent></Card>
        <Card className="border-0 shadow-lg"><CardContent className="p-6">
          <div className="flex items-center justify-between mb-2"><div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center"><RefreshCw className="w-5 h-5 text-purple-600" /></div></div>
          <p className="text-sm text-gray-500">{T.autoSync}</p>
          <p className="text-2xl font-bold text-purple-700">{autoSyncCount}</p>
        </CardContent></Card>
      </div>

      <Card className="border-0 shadow-lg">
        <div className="p-6">
          <div className="relative mb-6">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input type="text" placeholder={T.search} value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pr-10 pl-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-cyan-500 transition" />
          </div>
          {loading ? (
            <div className="p-12 text-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-600 mx-auto"></div><p className="text-gray-500 mt-4">{T.loading}</p></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center"><Link2 className="w-16 h-16 text-gray-300 mx-auto mb-4" /><h3 className="text-lg font-semibold text-gray-900 mb-2">{T.noIntegrations}</h3></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-100">
                  <tr>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thStore}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thPlatform}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thUrl}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thSync}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thStatus}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thOrders}</th>
                    <th className="text-right py-4 px-5 text-sm font-semibold text-gray-600">{T.thActions}</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((integration) => (
                    <tr key={integration.id} className="border-b border-gray-50 hover:bg-gray-50/50 transition">
                      <td className="py-4 px-5">
                        <div>
                          <p className="font-semibold text-gray-900">{integration.storeName}</p>
                          <p className="text-xs text-gray-500">API: {maskSecretStatus(integration.hasApiKey)}</p>
                        </div>
                      </td>
                      <td className="py-4 px-5"><span className="px-3 py-1 bg-cyan-50 text-cyan-700 rounded-full text-xs font-semibold">{integration.platform}</span></td>
                      <td className="py-4 px-5"><a href={integration.storeUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline text-sm truncate max-w-xs block">{integration.storeUrl}</a></td>
                      <td className="py-4 px-5">
                        {integration.autoSync ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-50 text-purple-700 rounded-full text-xs font-semibold">
                            <RefreshCw className="w-3 h-3" />
                            {integration.syncIntervalMinutes} {T.minutes}
                          </span>
                        ) : (
                          <span className="text-gray-500 text-sm">{T.disabled}</span>
                        )}
                      </td>
                      <td className="py-4 px-5">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${integration.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                          {integration.isActive ? T.active : T.inactive}
                        </span>
                      </td>
                      <td className="py-4 px-5"><span className="font-bold text-blue-600">{integration.totalOrdersSynced.toLocaleString()} {T.orders}</span></td>
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-1">
                          <button onClick={() => handleSync(integration.id)} disabled={syncingId === integration.id} className="p-2 hover:bg-cyan-50 rounded-lg text-gray-500 hover:text-cyan-600 transition disabled:opacity-50" title={T.sync}>
                            {syncingId === integration.id ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-cyan-600"></div> : <RefreshCw className="w-4 h-4" />}
                          </button>
                          <button onClick={() => openView(integration)} className="p-2 hover:bg-cyan-50 rounded-lg text-gray-500 hover:text-cyan-600 transition" title={T.view}><Eye className="w-4 h-4" /></button>
                          <button onClick={() => openEdit(integration)} className="p-2 hover:bg-cyan-50 rounded-lg text-gray-500 hover:text-cyan-600 transition" title={T.edit}><Edit className="w-4 h-4" /></button>
                          <button onClick={() => handleDelete(integration.id)} className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600 transition" title={T.delete}><Trash2 className="w-4 h-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{editingIntegration ? T.editIntegration : T.createIntegration}</h2>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div><Label>{T.lblPlatform} *</Label><select value={form.platform} onChange={(e) => setForm({...form, platform: e.target.value})} className={inputClass}><option value="Shopify">Shopify</option><option value="WooCommerce">WooCommerce</option><option value="Magento">Magento</option><option value="PrestaShop">PrestaShop</option><option value="Other">أخرى</option></select></div>
                <div><Label>{T.lblStoreName} *</Label><Input value={form.storeName} onChange={(e: any) => setForm({...form, storeName: e.target.value})} required className={inputClass} /></div>
                <div className="md:col-span-2"><Label>{T.lblStoreUrl} *</Label><Input value={form.storeUrl} onChange={(e: any) => setForm({...form, storeUrl: e.target.value})} required className={inputClass} placeholder="https://store.example.com" /></div>
                
                <div className="md:col-span-2">
                  <Button type="button" variant="outline" onClick={() => setShowApiFields(!showApiFields)} className="w-full flex items-center justify-center gap-2">
                    <Key className="w-4 h-4" />
                    {showApiFields ? T.hideApi : T.showApi}
                  </Button>
                </div>

                {showApiFields && (
                  <>
                    <div className="md:col-span-2 border-t pt-4">
                      <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                        <Lock className="w-4 h-4" />
                        {T.apiCredentials}
                      </h3>
                    </div>
                    <div><Label>{T.lblApiKey}</Label><Input value={form.apiKey} onChange={(e: any) => setForm({...form, apiKey: e.target.value})} className={inputClass} placeholder={T.placeholderApiKey} /></div>
                    <div><Label>{T.lblApiSecret}</Label>
                      <div className="relative">
                        <Input type={showApiSecret ? "text" : "password"} value={form.apiSecret} onChange={(e: any) => setForm({...form, apiSecret: e.target.value})} className={inputClass} placeholder={T.placeholderApiSecret} />
                        <button type="button" onClick={() => setShowApiSecret(!showApiSecret)} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div><Label>{T.lblAccessToken}</Label><Input value={form.accessToken} onChange={(e: any) => setForm({...form, accessToken: e.target.value})} className={inputClass} placeholder={T.placeholderAccessToken} /></div>
                    <div><Label>{T.lblWebhookUrl}</Label><Input value={form.webhookUrl} onChange={(e: any) => setForm({...form, webhookUrl: e.target.value})} className={inputClass} placeholder={T.placeholderWebhook} /></div>
                    <div className="md:col-span-2"><Label>{T.lblConfigData}</Label><textarea value={form.configData} onChange={(e: any) => setForm({...form, configData: e.target.value})} className={inputClass + ' font-mono text-sm'} rows={3} placeholder='{"key": "value"}' /></div>
                  </>
                )}

                <div><Label>{T.lblSyncInterval}</Label><Input type="number" value={form.syncIntervalMinutes} onChange={(e: any) => setForm({...form, syncIntervalMinutes: e.target.value})} className={inputClass} min="1" /></div>
                <div className="flex items-center gap-2"><input type="checkbox" id="intActive" checked={form.isActive} onChange={(e) => setForm({...form, isActive: e.target.checked})} className="w-4 h-4" /><label htmlFor="intActive" className="text-sm font-medium">{T.active}</label></div>
                <div className="flex items-center gap-2"><input type="checkbox" id="intAutoSync" checked={form.autoSync} onChange={(e) => setForm({...form, autoSync: e.target.checked})} className="w-4 h-4" /><label htmlFor="intAutoSync" className="text-sm font-medium">{T.autoSync}</label></div>
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowModal(false)} className="flex-1">{T.cancel}</Button>
                <Button type="submit" className="flex-1 bg-cyan-600 hover:bg-cyan-700">{editingIntegration ? T.update : T.create}</Button>
              </div>
            </form>
          </div></Card>
        </div>
      )}

      {showViewModal && selectedIntegration && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto"><div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">{T.viewIntegration}</h2>
              <button onClick={() => setShowViewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="pb-4 border-b mb-4">
              <h3 className="text-xl font-bold">{selectedIntegration.storeName}</h3>
              <p className="text-gray-500">{selectedIntegration.platform}</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2"><p className="text-sm text-gray-500">{T.thUrl}</p><a href={selectedIntegration.storeUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{selectedIntegration.storeUrl}</a></div>
              <div><p className="text-sm text-gray-500">{T.thStatus}</p><span className={`px-3 py-1 rounded-full text-xs font-semibold ${selectedIntegration.isActive ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>{selectedIntegration.isActive ? T.active : T.inactive}</span></div>
              <div><p className="text-sm text-gray-500">{T.thSync}</p><span className={`px-3 py-1 rounded-full text-xs font-semibold ${selectedIntegration.autoSync ? 'bg-purple-50 text-purple-700' : 'bg-gray-100 text-gray-500'}`}>{selectedIntegration.autoSync ? T.enabled : T.disabled}</span></div>
              {selectedIntegration.autoSync && <div><p className="text-sm text-gray-500">{T.lblSyncInterval}</p><p className="font-semibold">{selectedIntegration.syncIntervalMinutes} {T.minutes}</p></div>}
              <div><p className="text-sm text-gray-500">{T.lastSync}</p><p className="font-semibold">{formatDate(selectedIntegration.lastSyncAt)}</p></div>
              <div><p className="text-sm text-gray-500">{T.thOrders}</p><p className="font-bold text-blue-600 text-xl">{selectedIntegration.totalOrdersSynced.toLocaleString()} {T.orders}</p></div>
              
              {(selectedIntegration.hasApiKey || selectedIntegration.hasApiSecret || selectedIntegration.hasAccessToken || selectedIntegration.webhookUrl) && (
                <div className="col-span-2 border-t pt-4 mt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                    <Key className="w-4 h-4" />
                    {T.apiCredentials}
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div><p className="text-xs text-gray-500">API Key</p><p className="font-semibold text-sm bg-gray-50 p-2 rounded">{maskSecretStatus(selectedIntegration.hasApiKey)}</p></div>
                    <div><p className="text-xs text-gray-500">API Secret</p><p className="font-semibold text-sm bg-gray-50 p-2 rounded">{maskSecretStatus(selectedIntegration.hasApiSecret)}</p></div>
                    <div><p className="text-xs text-gray-500">Access Token</p><p className="font-semibold text-sm bg-gray-50 p-2 rounded">{maskSecretStatus(selectedIntegration.hasAccessToken)}</p></div>
                    {selectedIntegration.webhookUrl && <div className="col-span-2"><p className="text-xs text-gray-500">Webhook URL</p><p className="font-mono text-sm bg-gray-50 p-2 rounded truncate">{selectedIntegration.webhookUrl}</p></div>}
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-3 pt-4">
              <Button onClick={() => handleSync(selectedIntegration.id)} disabled={syncingId === selectedIntegration.id} className="flex-1 bg-cyan-600 hover:bg-cyan-700">
                {syncingId === selectedIntegration.id ? <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white ml-2"></div>{T.syncing}</> : <><RefreshCw className="w-4 h-4 ml-2" />{T.sync}</>}
              </Button>
              <Button onClick={() => { setShowViewModal(false); openEdit(selectedIntegration); }} variant="outline" className="flex-1"><Edit className="w-4 h-4 ml-2" />{T.edit}</Button>
              <Button onClick={() => setShowViewModal(false)} variant="outline" className="flex-1">{T.close}</Button>
            </div>
          </div></Card>
        </div>
      )}
    </div>
  );
}
